import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Mail, MessageCircle, ArrowDownRight } from "lucide-react";
import { LOGO, WHATSAPP_DISPLAY, INSTAGRAM, EMAIL } from "@/lib/catalog";
import useLenis from "@/hooks/useLenis";

import RecipeHero from "@/components/recipe/RecipeHero";
import RecipeMarquee from "@/components/recipe/RecipeMarquee";
import RecipeBenefits from "@/components/recipe/RecipeBenefits";
import RecipeShowcase from "@/components/recipe/RecipeShowcase";
import RecipeTestimonials from "@/components/recipe/RecipeTestimonials";
import CheckoutDrawer from "@/components/recipe/CheckoutDrawer";

const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function RecipeLanding() {
  const [open, setOpen] = useState(false);
  useLenis(true);

  return (
    <main data-testid="recipe-page" className="bg-white text-black">
      <RecipeHero onCta={() => setOpen(true)} />
      <RecipeMarquee />
      <RecipeBenefits />
      <RecipeShowcase />
      <RecipeTestimonials />

      {/* Final CTA */}
      <section id="comprar" data-testid="recipe-final-cta" className="border-y border-black bg-black text-white">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em]">
              <span className="inline-block w-10 h-px bg-white" />
              <span>Edição I · Lançamento</span>
            </div>
            <h2 className="font-display mt-6 text-5xl sm:text-7xl md:text-[128px] leading-[0.9] tracking-[-0.03em]">
              Faça você<br />
              <span className="italic">mesma.</span>
            </h2>
            <div className="mt-10 md:mt-16 grid md:grid-cols-12 gap-10 items-end">
              <p className="md:col-span-6 text-base md:text-lg leading-relaxed max-w-xl">
                A receita exata, as proporções exatas, os erros que eu já cometi pra você
                não cometer. Em PDF, pra sempre com você.
              </p>
              <div className="md:col-span-6 flex flex-col sm:flex-row md:justify-end items-start sm:items-end gap-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em]">Investimento</p>
                  <div className="flex items-baseline gap-3 mt-2">
                    <span className="text-sm line-through opacity-60">{fmtBRL(97)}</span>
                    <span className="font-display text-5xl md:text-6xl tracking-[-0.02em]">{fmtBRL(27.9)}</span>
                  </div>
                </div>
                <button
                  data-testid="final-cta-buy"
                  onClick={() => setOpen(true)}
                  className="btn-primary !bg-white !text-black !border-white hover:!bg-black hover:!text-white"
                >
                  Quero a receita
                  <ArrowDownRight size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-black border-t border-black">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-14 md:py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3">
                <img src={LOGO} alt="The Quick Brownie" className="h-10 w-auto" />
                <span className="font-display text-xl tracking-tight">The Quick Brownie</span>
              </div>
              <p className="mt-6 font-display text-3xl md:text-4xl leading-[0.95] tracking-[-0.02em]">
                Doçura que te move.
              </p>
            </div>
            <div className="md:col-span-4">
              <p className="text-xs uppercase tracking-[0.25em]">Fale com a gente</p>
              <ul className="mt-6 space-y-3 text-base">
                <li>
                  <a href={`https://wa.me/${WHATSAPP_DISPLAY.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 hover:underline underline-offset-4">
                    <MessageCircle size={18} /> {WHATSAPP_DISPLAY}
                  </a>
                </li>
                <li>
                  <a href={`https://instagram.com/${INSTAGRAM}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 hover:underline underline-offset-4">
                    <Instagram size={18} /> @{INSTAGRAM}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-3 hover:underline underline-offset-4">
                    <Mail size={18} /> {EMAIL}
                  </a>
                </li>
              </ul>
            </div>
            <div className="md:col-span-3">
              <p className="text-xs uppercase tracking-[0.25em]">Loja</p>
              <ul className="mt-6 space-y-3 text-base">
                <li><a href="/" className="hover:underline underline-offset-4">Brownies prontos</a></li>
                <li><a href="/#faq" className="hover:underline underline-offset-4">FAQ</a></li>
              </ul>
            </div>
          </div>
          <div className="hairline mt-14" />
          <div className="mt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs uppercase tracking-[0.25em]">
            <span>© {new Date().getFullYear()} The Quick Brownie · Todos os direitos reservados</span>
            <span>Sem açúcar · Sem glúten · Sem lactose · Com colágeno</span>
          </div>
        </div>
      </footer>

      <CheckoutDrawer open={open} onClose={() => setOpen(false)} price={27.9} anchor={97} />
    </main>
  );
}
